import type { Control, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

import { getAutomationDataAttr } from "@/utils/automationUtils";
import { MultiSelect2 } from "@/components/UI2/molecules/MultiSelect";
import type { ProviderType } from "@/core/types";
import { TextField2 } from "@/components/UI2/molecules/TextField";

import { CREDENTIALS_BY_PROVIDER_TYPE } from "../constants/credentials";

type CredentialsMultiSelectProps<T extends FieldValues & { cp_credentials: string[] }> = {
  control: Control<T>;
  isReadOnly?: boolean;
  providerType: ProviderType;
};

export function CredentialsMultiSelect<T extends FieldValues & { cp_credentials: string[] }>({
  control,
  isReadOnly = false,
  providerType,
}: CredentialsMultiSelectProps<T>) {
  return (
    <Controller
      control={control as Control<FieldValues & { cp_credentials: string[] }>}
      name="cp_credentials"
      render={({ field, fieldState }) => {
        const options = CREDENTIALS_BY_PROVIDER_TYPE[providerType] ?? [];

        if (isReadOnly) {
          return (
            <TextField2
              hasStrongLabel
              isReadOnly
              label="Credentials"
              mode="input"
              onChange={field.onChange}
              value={(field.value as string[]).join(", ")}
              {...getAutomationDataAttr("ProviderBasicInfoCredentials")}
            />
          );
        }

        // PCP and Care Manager have no predefined credential options — allow free-form text
        // instead of a disabled multiselect so admins can still record custom credentials.
        if (options.length === 0) {
          return (
            <TextField2
              errorMessage={fieldState.error?.message}
              hasStrongLabel
              isInvalid={!!fieldState.error}
              isRequired
              label="Credentials"
              mode="input"
              onChange={(v) => field.onChange(v ? parseCredentialsText(v) : [])}
              value={(field.value as string[]).join(", ")}
              {...getAutomationDataAttr("ProviderBasicInfoCredentials")}
            />
          );
        }

        const selectedValues = (field.value as string[]).filter((v) =>
          options.some((o) => o.id === v),
        );
        const disabledKeys =
          selectedValues.length >= MAX_CREDENTIAL_SELECTIONS
            ? options.filter((o) => !selectedValues.includes(o.id)).map((o) => o.id)
            : undefined;

        return (
          <MultiSelect2
            errorMessage={fieldState.error?.message}
            hasStrongLabel
            isInvalid={!!fieldState.error}
            isRequired={options.length > 0}
            label="Credentials"
            onChange={field.onChange}
            options={options}
            /** Strip legacy credentials not in options — react-aria tracks selection
             * via the controlled value Set, so unknown keys would persist in every onChange emission.
             */
            value={selectedValues}
            {...(disabledKeys ? { disabledKeys } : {})}
            {...getAutomationDataAttr("ProviderBasicInfoCredentials")}
          />
        );
      }}
    />
  );
}

const MAX_CREDENTIAL_SELECTIONS = 2;

/** Mirrors mapToFormValues split logic so "Credential A, Credential B" round-trips
 * correctly through the free-form text field → API comma-separated string → form array.
 */
const parseCredentialsText = (value: string): string[] =>
  value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
